import { AddPhotoAlternate, Publish } from "@mui/icons-material";
import { apiSlice } from "../services/apiSlice";
import { use } from "react";
import { update } from "lodash";

export const formsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCustomForms: builder.query({
      query: (params) => ({
        url: `/forms/`,
        params: params,
      }),
    }),

    getPrivateNotesById: builder.query({
      query: (params) => ({
        url: `/notes/${params?.id}`,
        params
      }),
    }),

    addNewNote: builder.mutation({
      query: (data) => ({
        url: `/notes/`,
        method: "POST",
        body: {
          ...data,
        },
      }),
    }),

    updateNote: builder.mutation({
      query: (data) => ({
        url: `/notes/${data?.id}`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
    }),


    deleteCustomForm: builder.mutation({
      query: (ids: number[]) => {
        const encodedIds = encodeURIComponent(ids.join(",") + ",");
        return {
          url: `/forms/bulk-delete?ids=${encodedIds}`,
          method: "DELETE",
        };
      },
    }),
    updateContact: builder.mutation({
      query: (data) => ({
        url: `/contacts/${data?.id}`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
    }),
    // CustomForm Builder
    createCustomForm: builder.mutation({
      query: (data) => ({
        url: `/forms/initiate-form`,
        method: "POST",
        body: {
          ...data,
        },
      }),
    }),
    updateCustomForm: builder.mutation({
      query: (data) => ({
        url: `/forms/${data?.id}`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
    }),
    getCustomFormById: builder.query({
      query: (id) => ({
        url: `/forms/${id}`,
      }),
    }),
    publishCustomForm: builder.mutation({
      query: (id: number) => ({
        url: `/forms/publish-form/${id}`,
        method: "POST",
      }),
    }),
    duplicateCustomForm: builder.mutation({
      query: (id: number) => ({
        url: `/forms/duplicate-form/${id}`,
        method: "POST",
      }),
    }),
    getAllFields: builder.query({
      query: (params) => ({
        url: `/fields`,
        params: params,
      }),
    }),
    getAllFieldsAllPages: builder.query<any[], { query?: string; scopeId?: string; isStandard?: boolean; importData?: boolean; perPage?: number }>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        let page = 1;
        const perPage = arg.perPage ?? 50;
        let hasMore = true;
        let allResults: any[] = [];

        while (hasMore) {
          const result: any = await fetchWithBQ({
            url: "/fields",
            params: {
              query: arg.query || "",
              sort: "createdAt",
              sortDir: "ASC",
              scopeId: arg.scopeId,

              ...(typeof arg.importData !== "undefined" && { importData: arg.importData }),
              ...(typeof arg.isStandard !== "undefined" && { isStandard: arg.isStandard }),
              page,
              perPage,
            },
          });

          if (result.error) return { error: result.error };

          const fields = result.data?.fields ?? [];
          const totalCount = result.data?.totalCount ?? 0;
          const serverPerPage = result.data?.perPage ?? perPage;
          const serverPage = result.data?.page ?? page;
          allResults = [...allResults, ...fields];

          if (!fields.length) {
            hasMore = false;
            break;
          }

          hasMore = totalCount
            ? serverPage * serverPerPage < totalCount
            : fields.length === serverPerPage;

          page = serverPage + 1;
        }

        return { data: allResults };
      },
      providesTags: (result: any) => {
        // result may be an array (allResults) or an object depending on how the response
        // is shaped by the baseQuery. Normalize to an array before mapping.
        const items = Array.isArray(result) ? result : [result];

        return items && items.length
          ? [
            { type: 'fields', id: 'LIST' },
            ...items.map(({ id }: any) => ({ type: 'fields' as const, id })),
          ]
          : [{ type: 'fields', id: 'LIST' }];
      },
    }),
    getAllDataTypes: builder.query({
      query: (params) => ({
        url: `/fields/data-types`,
        params: params,
      }),
    }),
    getFieldById: builder.query({
      query: (id) => ({
        url: `/fields/${id}`,
      }),
    }),
    // Check access key availability
    // Accepts an object so callers can pass a fieldId to exclude the current field
    // when validating availability during updates.
    getAccessKeyAvailability: builder.query<{ fieldExist: boolean }, { accessKey: string; fieldId?: string | number }>({
      query: ({ accessKey, fieldId }) => ({
        url: `/fields/access-key`,
        method: 'GET',
        params: { accessKey, fieldId },
      }),
    }),
    addPage: builder.mutation({
      query: (data) => ({
        url: `/forms/create-page`,
        method: "POST",
        body: {
          ...data,
        },
      }),
    }),
    updatePage: builder.mutation({
      query: (data) => ({
        url: `/forms/form-page/${data?.id}`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
    }),
    deletePage: builder.mutation({
      query: (id: number) => ({
        url: `/forms/form-page/${id}`,
        method: 'DELETE',
      }),
    }),
    createField: builder.mutation({
      query: (data) => ({
        url: `/fields/attach/field-to-form`,
        method: "POST",
        body: {
          ...data,
        },
      }),
    }),
    duplicateFormField: builder.mutation({
      query: (data) => ({
        url: `/fields/duplicate-form-field`,
        method: "POST",
        body: {
          ...data,
        },
      }),
    }),
    updatePropertiesOfField: builder.mutation({
      query: (data) => ({
        url: `/fields/settings/${data?.id}`,
        method: "PUT",
        body: {
          newSettings: data.settings,
        },
      }),
    }),
    deleteField: builder.mutation({
      query: (id: number) => ({
        url: `/fields/field-from-form/${id}`,
        method: 'DELETE',
      }),
    }),
    // Create Custom Field
    createCustomFieldTemplate: builder.mutation({
      query: (data: any) => ({
        url: `/fields`,
        method: "POST",
        body: {
          ...data,
        },
      }),
      invalidatesTags: [{ type: 'fields', id: 'LIST' }],

    }),
    updateCustomFieldTemplate: builder.mutation({
      query: (data: any) => ({
        url: `/fields/${data?.id}`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
      invalidatesTags: (result, error, data) => [{ type: 'fields', id: 'LIST' }],
    }),
    // Delete Custom Field
    deleteCustomFieldTemplate: builder.mutation({
      query: (id: number) => ({
        url: `/fields/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'fields', id }],
    }),
    //Duplicate Custom Field
    duplicateCustomFieldTemplate: builder.mutation({
      query: (id: number) => ({
        url: `/fields/duplicate-field/${id}`,
        method: "POST",

      }),
    }),
    getAllComponents: builder.query({
      query: (params) => ({
        url: `/components`,
        params: params,
      }),
      providesTags: (result) => {
        // result might be an array of components or an object like { components: [...] }
        const items = Array.isArray(result)
          ? result
          : (result?.components ?? result?.data ?? []);

        return items && items.length
          ? [
            { type: 'components', id: 'LIST' },
            ...items.map((c: any) => ({ type: 'components' as const, id: c?.id })),
          ]
          : [{ type: 'components', id: 'LIST' }];
      },
    }),
    getComponentById: builder.query({
      query: (id) => ({
        url: `/components/${id}`,
      }),
    }),
    addNewComponent: builder.mutation({
      query: (data) => ({
        url: `/components`,
        method: "POST",
        body: {
          ...data,
        },
      }),
      invalidatesTags: [{ type: 'components', id: 'LIST' }],
    }),
    deleteComponent: builder.mutation({
      query: (id: number) => ({
        url: `/components/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'components', id }],
    }),
    duplicateComponent: builder.mutation({
      query: (id: number) => ({
        url: `/components/duplicate-component/${id}`,
        method: "PUT",
      }),
    }),
    updateComponent: builder.mutation({
      query: (data) => ({
        url: `/components/${data?.id}`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'components', id: arg?.id }],
    }),
    applyConditionalLogicToField: builder.mutation({
      query: (data) => ({
        url: `/fields/${data?.controllingFieldId}/conditions`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
    }),
    getFormData: builder.query({
      query: ({ accessKey }) => ({
        url: `/forms/module`,
        method: "GET",
        params: { accessKey },
      }),
    }),
    // Fetch a form definition along with populated values for a specific record.
    // Optionally accept a formId to force loading a particular form when multiple
    // forms may be associated with the same record (e.g., onboarding flows).
    getFormWithValues: builder.query({
      query: ({ scope, recordId, formId }: { scope: string; recordId: number | string; formId?: number | string }) => ({
        url: `/forms/${scope}/${recordId}`,
        method: "GET",
        params: formId ? { formId } : undefined,
      }),
    }),
    submitForm: builder.mutation({
      query: (data) => ({
        url: `/forms/submission`,
        method: "POST",
        body: {
          ...data,
        },
      }),
      invalidatesTags: ["Contacts"],
    }),
    getPresignedUrl: builder.mutation({
      query: (data) => ({
        url: `/search-query/presigned-url`,
        method: "POST",
        body: {
          ...data,
        },
      }),
    }),
    attFromToModule: builder.mutation({
      query: (data) => ({
        url: `/forms/module`,
        method: "POST",
        body: {
          ...data,
        },
      }),
    }),
    getScopesforFields: builder.query({
      query: (params) => ({
        url: `/scopes`,
        method: "GET",
        params: params,
      }),
      transformResponse: (response: any) => {
        return { ...response, options: response?.scopes?.filter((item: any) => item?.displayName !== "Event")?.map((item: any) => ({ label: item?.displayName, value: item?.id })) };
      }
    }),
  }),
});

export const {
  useGetCustomFormsQuery,
  useUpdateContactMutation,
  useUpdateNoteMutation,
  useGetPrivateNotesByIdQuery,
  useAddNewNoteMutation,
  useDeleteCustomFormMutation,
  useCreateCustomFormMutation,
  useUpdateCustomFormMutation,
  useGetCustomFormByIdQuery,
  useLazyGetCustomFormByIdQuery,
  useLazyGetCustomFormsQuery,
  usePublishCustomFormMutation,
  useDuplicateCustomFormMutation,
  useGetAllFieldsQuery,
  useGetAllFieldsAllPagesQuery,
  useGetAllDataTypesQuery,
  useGetAccessKeyAvailabilityQuery,
  useLazyGetAccessKeyAvailabilityQuery,
  useGetFieldByIdQuery,
  useAddPageMutation,
  useUpdatePageMutation,
  useDeletePageMutation,
  useCreateFieldMutation,
  useDuplicateFormFieldMutation,
  useUpdatePropertiesOfFieldMutation,
  useDeleteFieldMutation,
  useCreateCustomFieldTemplateMutation,
  useUpdateCustomFieldTemplateMutation,
  useDeleteCustomFieldTemplateMutation,
  useDuplicateCustomFieldTemplateMutation,
  useGetAllComponentsQuery,
  useGetComponentByIdQuery,
  useAddNewComponentMutation,
  useDeleteComponentMutation,
  useDuplicateComponentMutation,
  useUpdateComponentMutation,
  useApplyConditionalLogicToFieldMutation,
  useSubmitFormMutation,
  useGetPresignedUrlMutation,
  useGetFormDataQuery,
  useGetFormWithValuesQuery,
  useAttFromToModuleMutation,
  useGetScopesforFieldsQuery,
} = formsApiSlice;
